import graphql__523d7ea104f4138fc2b5 from "./__generated__/ProfileEditionForm_user.graphql.js";
import graphql__6eca2ce393985730bc41 from "./__generated__/ProfileEditionForm_authenticationConfig.graphql.js";import { useCallback, useState } from "react";
import { graphql, useFragment } from "react-relay";
import { useDispatch } from "react-redux";
import { defineMessages } from "react-intl";

import { intlMessages as formPageIntlMessages } from "../ParticipativeModule/FormPage";
import withFormStore from "../Form/withFormStore";
import { useAlert } from "../Alerts/withAlert";
import { ALERT_TYPE_ERROR, ALERT_TYPE_SUCCESS } from "../Alerts/Alert";
import { useFormatMessage } from "../Intl/Context";
import { isAuthorized } from "../Utils/authorizations";
import StringField from "../Form/Field/StringField";
import EmailField from "../Form/Field/EmailField";
import PasswordField from "../Form/Field/PasswordField";
import CheckBoxField from "../Form/Field/CheckBoxField";
import EmailTrackingConsentField from "../Form/Field/EmailTrackingConsentField";
import Button from "../Buttons/Button";
import Answers from "../Answer/Answers";
import PhoneNumberField from "./PhoneNumberField";
import { RevealHiddenReadOnlyFieldsContext } from "../Answer/RevealHiddenReadOnlyFieldsContext";

import { updateProfile } from "../../mutations/UpdateProfileMutation";
import { setViewer } from "../../reducers/viewer";

const ProfileEditionForm = props => {
  const user = useFragment(ProfileEditionForm_user, props.user);
  const authenticationConfig = useFragment(
    ProfileEditionForm_authenticationConfig,
    props.authenticationConfig
  );
  const {
    name,
    email,
    emailConsent,
    emailTrackingConsent,
    phoneNumber,
    answers,
    authorizations
  } = user;
  const { enableEmailConsent, emailConsentLabel } = authenticationConfig;
  const canEditPassword = isAuthorized(authorizations.allowEditingPassword);
  const isEmailRequired = isAuthorized(authorizations.requireEmail);
  const canDisplayPhoneNumber = isAuthorized(authorizations.displayPhoneNumber);
  const canUpdatePhoneNumber = isAuthorized(authorizations.updatePhoneNumber);

  const { showAlert } = useAlert();
  const { validateForm, formState, pushErrors } = props;
  const dispatch = useDispatch();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const t = useFormatMessage();
  const labels = {
    name: t(intlMessages.name),
    requiredFieldsHint: t(formPageIntlMessages.requiredFieldsHint),
    email: t(intlMessages.email),
    password: t(intlMessages.password),
    passwordHint: t(intlMessages.passwordHint),
    passwordConfirmation: t(intlMessages.passwordConfirmation),
    customFieldsSectionTitle: t(intlMessages.customFieldsSectionTitle),
    consentsSectionTitle: t(intlMessages.consentsSectionTitle),
    update: t(intlMessages.update),
    success: t(intlMessages.success),
    successWithEditedEmail: t(intlMessages.successWithEditedEmail)
  };

  const onUpdateResponse = useCallback(
    response => {
      setIsSubmitting(false);

      if (response.updateProfile.success) {
        if (response.updateProfile.didUpdateEmail) {
          showAlert({
            message: labels.successWithEditedEmail,
            type: ALERT_TYPE_SUCCESS
          });
        } else {
          showAlert({
            message: labels.success,
            type: ALERT_TYPE_SUCCESS
          });
        }

        // Update viewer data with updated user
        dispatch(setViewer(response.updateProfile.user));
      } else {
        pushErrors(response.updateProfile.errors);

        showAlert({
          message: response.updateProfile.notice,
          type: ALERT_TYPE_ERROR
        });
      }
    },
    [dispatch, labels.success, labels.successWithEditedEmail, pushErrors, showAlert]
  );

  const onSubmit = useCallback(
    e => {
      e.preventDefault();

      if (validateForm()) {
        setIsSubmitting(true);

        updateProfile(formState, onUpdateResponse);
      }
    },
    [formState, onUpdateResponse, validateForm]
  );

  return (
    <form onSubmit={onSubmit} data-profile-edition-form>
      <p
        className="form-hint-required-fields margin-bottom-sm text-small"
        dangerouslySetInnerHTML={{ __html: labels.requiredFieldsHint }}
      />

      <div className="row">
        <div className="col-md-6">
          <StringField
            fieldName="name"
            required
            label={labels.name}
            defaultValue={name}
            autoComplete="nickname"
          />
        </div>
        <div className="col-md-6">
          <EmailField
            fieldName="email"
            required={isEmailRequired}
            label={labels.email}
            defaultValue={email}
            autoComplete="email"
          />
        </div>
      </div>

      {canEditPassword && (
        <div className="row">
          <div className="col-md-6">
            <PasswordField
              fieldName="password"
              label={labels.password}
              autoComplete="new-password"
            />
          </div>
          <div className="col-md-6">
            <PasswordField
              fieldName="passwordConfirmation"
              label={labels.passwordConfirmation}
              confirmationOf="password"
              autoComplete="new-password"
            />
          </div>
        </div>
      )}

      {canDisplayPhoneNumber && (
        <PhoneNumberField phoneNumber={phoneNumber} canUpdate={canUpdatePhoneNumber} />
      )}

      {answers.length > 0 && (
        <>
          <hr />

          <h3 className="text-subtitle-page margin-bottom-sm">
            {labels.customFieldsSectionTitle}
          </h3>

          {/* The profile page is the only surface displaying the read-only
              fields hidden in the back-office, so the user can review the
              data their identity provider supplied. */}
          <RevealHiddenReadOnlyFieldsContext.Provider value={true}>
            <Answers answers={answers} />
          </RevealHiddenReadOnlyFieldsContext.Provider>
        </>
      )}

      <hr />

      <h3 className="text-subtitle-page margin-bottom-sm">
        {labels.consentsSectionTitle}
      </h3>

      {/* Tracking consent is always collected, as the CNIL requires it independently of
          the platform's own email consent setting. */}
      <div className="checkboxes-no-background">
        {enableEmailConsent && (
          <CheckBoxField
            fieldName="emailConsent"
            label={emailConsentLabel}
            defaultChecked={emailConsent}
          />
        )}

        <EmailTrackingConsentField defaultChecked={emailTrackingConsent} />
      </div>

      <Button
        type="submit"
        className="btn btn-primary btn-md"
        label={labels.update}
        iconClassName="icon-validate"
        iconPosition="after"
        disabled={isSubmitting}
        data-loading={isSubmitting}
        data-profile-edition-form-submit
      />
    </form>
  );
};

const ProfileEditionForm_user = graphql__523d7ea104f4138fc2b5;

const ProfileEditionForm_authenticationConfig = graphql__6eca2ce393985730bc41;

export default withFormStore(ProfileEditionForm);

export const intlMessages = defineMessages({
  name: {
    id: "profile.form.name",
    defaultMessage: "Pseudo"
  },
  email: {
    id: "profile.form.email",
    defaultMessage: "Adresse email"
  },
  password: {
    id: "profile.form.password",
    defaultMessage: "Mot de passe"
  },
  passwordHint: {
    id: "profile.form.passwordHint",
    defaultMessage: `
      Laissez vide si vous ne souhaitez pas modifier votre mot de passe
    `
  },
  passwordConfirmation: {
    id: "profile.form.passwordConfirmation",
    defaultMessage: "Confirmation du mot de passe"
  },
  customFieldsSectionTitle: {
    id: "profile.form.customFieldsSectionTitle",
    defaultMessage: "Autres champs"
  },
  consentsSectionTitle: {
    id: "profile.form.consentsSectionTitle",
    defaultMessage: "Consentements"
  },
  update: {
    id: "profile.form.update",
    defaultMessage: "Mettre à jour"
  },
  success: {
    id: "profile.form.success",
    defaultMessage: "Votre profil a bien été mis à jour !"
  },
  successWithEditedEmail: {
    id: "profile.edit.successWithEditedEmail",
    defaultMessage: `Votre profil a bien été mis à jour et validé !
    Votre nouvelle adresse e-mail est en attente de confirmation.
    Merci de la confirmer à l'aide du lien qui vous a été envoyé sur votre nouvelle adresse e-mail.`
  }
});
